import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeApiKeysAPI } from '../services/api';
import { TimeApiKey } from '../types';
import TableLoadingRow from './TableLoadingRow';

const formatDate = (value?: string) => value ? new Date(value).toLocaleString() : '—';

const AdminApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<TimeApiKey[]>([]);
  const [loading, setLoading] = useState(true);

  const loadKeys = async () => {
    setLoading(true);
    try {
      setKeys(await timeApiKeysAPI.getAllKeys());
    } catch {
      toast.error('API keys could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const revokeKey = async (key: TimeApiKey) => {
    if (!window.confirm(`Revoke API key "${key.name}" for ${key.userEmail || key.userId}?`)) {
      return;
    }

    try {
      await timeApiKeysAPI.revokeAnyKey(key.id);
      await loadKeys();
      toast.success('API key revoked');
    } catch {
      toast.error('API key could not be revoked');
    }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>All API keys</h1>
          <p>Admin view of API keys created by all time tracking users.</p>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table time-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Name</th>
              <th>Prefix</th>
              <th>Role</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableLoadingRow colSpan={8} label="Loading all API keys" />
            ) : keys.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted">No API keys</td></tr>
            ) : keys.map(key => (
              <tr key={key.id}>
                <td data-label="User">{key.userEmail || key.userId}</td>
                <td data-label="Name">{key.name}</td>
                <td data-label="Prefix"><code>{key.keyPrefix}…</code></td>
                <td data-label="Role">{key.role}</td>
                <td data-label="Created">{formatDate(key.createdDate)}</td>
                <td data-label="Last used">{formatDate(key.lastUsedDate)}</td>
                <td data-label="Status">{key.isActive ? 'Active' : 'Revoked'}</td>
                <td data-label="Actions">
                  <button
                    className="btn btn-outline-danger btn-sm"
                    type="button"
                    onClick={() => revokeKey(key)}
                    disabled={!key.isActive}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminApiKeysPage;
